/* eslint-disable no-bitwise */
import SmpteTimecode from 'smpte-timecode';
import type { Fraction } from './fractionType';
import { getFrameDurationFromFrameRateApproximation } from './framerate';
import { approximateFractionValue, approximateNumber, divideFractions, frac, invertFraction, multiplyFractions } from './integerMath';
import isInteger from 'lodash/isInteger';
import { invariant } from './invariant';
import { makeValidationFunction, validateBooleanProperty, validateNumberProperty } from '../util/validation';
import type { Logger } from '../types/logger';
import { exceptionToString } from './exception';
import moment from 'moment-timezone';
import { safeArrayAccess } from './safeArrayAccess';

type FrameRates = 23.976 | 24 | 25 | 29.97 | 30 | 50 | 59.94 | 60;
type SupportedFpsKeyType = typeof fpsMapForTcLibKeys[number];

const fpsMapForTcLibKeys = <const> [2397, 2400, 2500, 2997, 3000, 5000, 5994, 6000];
const fpsMapForTcLib: Record<SupportedFpsKeyType, FrameRates> = {
  2397: 23.976,
  2400: 24,
  2500: 25,
  2997: 29.97,
  3000: 30,
  5000: 50,
  5994: 59.94,
  6000: 60,
};

export function isDropFrameTc(timecode: string | undefined): boolean | undefined {
  return timecode !== undefined ? timecode.includes(';') : undefined;
}

/**
 * Normalizes a timecode string from non-standard format (all semicolons like "00;00;00;00")
 * to standard SMPTE format ("00:00:00;00" for drop-frame).
 * Some video files store timecode metadata with semicolons for all separators.
 */
export function normalizeTimecodeString(timecode: string): string {
  return timecode.replace(/^(\d{2});(\d{2});(\d{2});(\d{2})$/, '$1:$2:$3;$4');
}

function getClosestFpsKey(mediaFrameRate: number): SupportedFpsKeyType {
  const closest = fpsMapForTcLibKeys.reduce((prev, curr) => {
    return (Math.abs(curr - mediaFrameRate) < Math.abs(prev - mediaFrameRate) ? curr : prev);
  });

  return closest;
}

function getTcInternal(input: string | Date, mediaFrameRate: number, dropFrame: boolean | undefined = undefined) {
  const fpsKeyFromInput = Math.trunc(mediaFrameRate * 100);
  const fpsKey = getClosestFpsKey(fpsKeyFromInput);

  if (fpsKeyFromInput !== fpsKey) {
    console.warn(`Taking nearest fps key ${fpsKey} as ${fpsKeyFromInput} is not supported`);
  }

  const fpsForTcLib = fpsMapForTcLib[fpsKey];
  const normalizedInput = typeof input === 'string' ? normalizeTimecodeString(input) : input;
  const tc = SmpteTimecode(normalizedInput, fpsForTcLib, dropFrame);
  return tc;
}

/**
 * The subset of SmpteTimecode.TimecodeInstance that we wish to expose to other
 * projects without requiring them also to pull in the smpte-timecode package.
 */
interface TimecodeInstance {
  toString(): string;
}

export function timecodeToMillisecond(tc: string, frameRate: Fraction, logger?: Logger): number | null {
  try {
    const normalizedTc = normalizeTimecodeString(tc);
    const stc = new SmpteTimecode(normalizedTc, [frameRate.numerator, frameRate.denominator], isDropFrameTc(normalizedTc));
    return (stc.frameCount / stc.frameRate) * 1000.0;
  } catch(err: unknown) {
    logger?.warn(`Error while trying to get millisecond from timecode ${tc}, frameRate: ${frameRate.numerator}/${frameRate.denominator}: ${exceptionToString(err)}`);
    return null;
  }
}

export function millisecondToTimecode(ms: number, frameRate: Fraction, logger?: Logger): string | null {
  try {
    if (frameRate.denominator !== 1 && frameRate.denominator !== 1001) {
      return null;
    }
    const fc = Math.round(approximateFractionValue(frameRate) * ms / 1000.0);
    const df = frameRate.denominator === 1001;
    const stc = new SmpteTimecode(fc, [frameRate.numerator, frameRate.denominator], df);
    return stc.toString();
  } catch(err: unknown) {
    logger?.warn(`Error while trying to get timecode from millisecond ${ms}, frameRate: ${frameRate.numerator}/${frameRate.denominator}: ${exceptionToString(err)}`);
    return null;
  }
}

export function fractionToTimecode(s: Fraction, frameRate: Fraction, logger?: Logger): string | null {
  try {
    if (frameRate.denominator !== 1 && frameRate.denominator !== 1001) {
      return null;
    }
    const fc = multiplyFractions(s, frameRate);
    const df = frameRate.denominator === 1001;
    const stc = new SmpteTimecode(approximateFractionValue(fc), [frameRate.numerator, frameRate.denominator], df);
    return stc.toString();
  } catch(err: unknown) {
    logger?.warn(`Error while trying to get timecode from millisecond ${s}, frameRate: ${frameRate.numerator}/${frameRate.denominator}: ${exceptionToString(err)}`);
    return null;
  }
}

export function getTimecodeInTimezone(timezone: string | undefined, frameRate: Fraction, logger: Logger) {
  try {
    let msSinceMidnight: number;
    if (!timezone) {
      const nw = new Date();
      // Let's set hours, minutes, seconds etc to midnight i.e., 0
      const mn = new Date(nw.getFullYear(), nw.getMonth(), nw.getDate());
      msSinceMidnight = nw.getTime() - mn.getTime();
    } else {
      const n = moment.tz(timezone);
      // Let's set hours, minutes, seconds etc to midnight i.e., 0
      const m = moment.tz(n.format('YYYY-MM-DD'), timezone);
      msSinceMidnight = n.diff(m);
    }

    return millisecondToTimecode(msSinceMidnight, frameRate, logger);
  } catch(err: unknown) {
    logger.warn(`Error while trying to get timecode for timezone ${timezone}, frameRate: ${frameRate.numerator}/${frameRate.denominator}: ${exceptionToString(err)}`);
    return null;
  }
}

export function getTc(millisecondsOrTimecode: number | string, mediaFrameRate: number, dropFrame: boolean | undefined = undefined): TimecodeInstance {
  const typeOfInput = typeof millisecondsOrTimecode;
  if (typeOfInput === 'string') {
    return getTcInternal(millisecondsOrTimecode as string, mediaFrameRate, dropFrame);
  }

  const tcDate = new Date();
  tcDate.setHours(0, 0, 0, millisecondsOrTimecode as number);
  return getTcInternal(tcDate, mediaFrameRate, dropFrame);
}

// Modifies and returns the input SmpteTimecode object after adding the timecode specified by offsetTcString
export function addTc(tc: any, offsetTcString: string | undefined) {
  if (!offsetTcString) {
    return tc;
  }

  const offsetTc = getTc(offsetTcString, tc.frameRate, tc.dropFrame);

  tc.add(offsetTc);
  return tc;
}

// used to get time of start frame (floor)
export function getStartFrameMillisecondsInclusive(milliseconds: number, mediaFrameRate: number) {
  const frames = milliseconds * mediaFrameRate / 1000;
  milliseconds = Math.trunc(frames) * 1000 / mediaFrameRate;
  return milliseconds;
}

// used to get a time beyond the end frame, as ffmpeg excludes the ending frame from the cut
export function getEndFrameMillisecondsExclusive(milliseconds: number, mediaFrameRate: number) {
  const frames = milliseconds * mediaFrameRate / 1000;
  milliseconds = Math.trunc(frames + 1) * 1000 / mediaFrameRate;
  return milliseconds;
}

// used to get the time of end frame for TC range purpose, as we want to show the last included frame in titles
export function getEndFrameMillisecondsInclusive(milliseconds: number, mediaFrameRate: number) {
  const frames = milliseconds * mediaFrameRate / 1000;
  milliseconds = Math.trunc(frames) * 1000 / mediaFrameRate;
  return milliseconds;
}



/**
 * @deprecated Use getTimeInSecondsFromTimecodeString instead as this function does not take into account the frame rate
 * and discards frame number from the timecode string.
 */
export const getMsFromTimecodeString = (logger: Logger, tc?: string) => {
  if (!tc) return null;

  let normalizedTc = normalizeTimecodeString(tc);
  if (isDropFrameTc(normalizedTc)) {
    normalizedTc = normalizedTc.substr(0, normalizedTc.indexOf(';'));
  }
  const parts = normalizedTc.split(':');
  try {
    const hoursMs = parseInt(safeArrayAccess(parts, 0)) * 3600000;
    const minsMs = parseInt(safeArrayAccess(parts, 1)) * 60000;
    const secsMs = parseInt(safeArrayAccess(parts, 2)) * 1000;
    return hoursMs + minsMs + secsMs;
  } catch (e) {
    logger.error(`Failed parsing timecode: ${tc}`);
    return null;
  }
};

type TimecodeObject = ReturnType<typeof SmpteTimecode>;

/**
 * Converts a timecode string to duration in seconds. Returns null if timecode + frame rate combination is not supported.
 */
export function getTimeInSecondsFromTimecodeString(logger: Logger, timecode: string, mediaFrameRate: number, startTimeOffsetInSeconds = 0): Fraction | null {
  const info = tryGetPreciseTimecodeInfo(logger, timecode, mediaFrameRate, Boolean(isDropFrameTc(timecode)));
  if (!info) return null;

  const { timecodeObj, frameRate, frameDuration } = info;
  let startTimeOffsetInFrameCount = 0;
  if (startTimeOffsetInSeconds > 0) {
    const fracStartTimeOffsetInSeconds = approximateNumber(startTimeOffsetInSeconds, 2);
    startTimeOffsetInFrameCount = approximateFractionValue(divideFractions(fracStartTimeOffsetInSeconds, frameDuration));
  }
  if (startTimeOffsetInFrameCount) timecodeObj.add(startTimeOffsetInFrameCount);

  return frac(timecodeObj.frameCount * frameRate.denominator, frameRate.numerator);
}

export function tryGetPreciseTimecodeInfo(logger: Logger, timecode: string, mediaFrameRate: number, isDropFrame: boolean): {
  timecodeObj: TimecodeObject,
  frameRate: Fraction,
  frameDuration: Fraction,
} | null {
  try {
    const frameDuration = getFrameDurationFromFrameRateApproximation(mediaFrameRate);
    if (!frameDuration.precise) return null;

    const frameRate = invertFraction(frameDuration.precise);
    const normalizedTimecode = normalizeTimecodeString(timecode);
    return {
      timecodeObj: SmpteTimecode(normalizedTimecode, [frameRate.numerator, frameRate.denominator], isDropFrame),
      frameRate,
      frameDuration: frameDuration.precise,
    };
  } catch (error) {
    logger.error(`Failed parsing timecode: ${timecode} ${exceptionToString(error)}`);
    return null;
  }
}

export interface Timecode {
  frames: number,
  seconds: number,
  minutes: number,
  hours: number,
  dropFrame: boolean,
}

export function timecodeToFrameCount(timecode: Timecode, frameRate: Fraction): number {
  const smpteTimecode =
    new SmpteTimecode(
      timecode,
      [frameRate.numerator, frameRate.denominator],
      timecode.dropFrame
    );

  return smpteTimecode.frameCount;
}

export function frameCountToTimecode(
  frameCount: number,
  frameRate: Fraction,
  isDropFrame: boolean
): Timecode {
  const smpteTimecode =
    new SmpteTimecode(
      frameCount,
      [frameRate.numerator, frameRate.denominator],
      isDropFrame
    );

  return {
    frames: smpteTimecode.frames,
    seconds: smpteTimecode.seconds,
    minutes: smpteTimecode.minutes,
    hours: smpteTimecode.hours,
    dropFrame: isDropFrame,
  };
}

/**
 *  Serialize time code string of the form: hh:mm:ss[:;]ff. The use of ";"
 *  indicates drop frame mode
 */
export function timecodeToString(timecode: Timecode): string {
  const firstPart =
    [timecode.hours, timecode.minutes, timecode.seconds]
      .map((n) => n.toString().padStart(2, '0'))
      .join(':');

  return `${firstPart}${timecode.dropFrame ? ';' : ':'}${timecode.frames.toString().padStart(2, '0')}`;
}

/**
 *  Parse time code string of the form: hh:mm:ss[:;]ff. The use of ";"
 *  indicates drop frame mode
 */
export function parseTimecodeString(timecodeString: string): Timecode | null {
  const normalizedTimecode = normalizeTimecodeString(timecodeString);
  const c = normalizedTimecode.split(':');
  let dropFrame = false;

  // For drop frame convert ['hh', 'mm', 'ss;ff'] to
  // ['hh', 'mm', 'ss', 'ff]
  if (c.length === 3) {
    const secondsAndFrames = safeArrayAccess(c, 2).split(';');
    if (secondsAndFrames.length !== 2) {
      return null;
    }
    c.splice(2, 1, ...secondsAndFrames);
    dropFrame = true;
  }

  if (c.length !== 4) {
    return null;
  }
  const hours = parseFloat(safeArrayAccess(c, 0));
  const minutes = parseFloat(safeArrayAccess(c, 1));
  const seconds = parseFloat(safeArrayAccess(c, 2));
  const frames = parseFloat(safeArrayAccess(c, 3));
  if (!isInteger(hours) || !isInteger(minutes) || !isInteger(seconds) || !isInteger(frames)) {
    return null;
  }

  if ((hours < 0) || (minutes < 0) || (seconds < 0) || (frames < 0)) {
    return null;
  }

  if ((minutes >= 60) || (seconds >= 60)) {
    return null;
  }

  return { hours, minutes, seconds, frames, dropFrame };
}

/**
 *  Parses binary encoded timecode as specified in SMPTE ST 12-1
 */
export function parseTimecode(bytes: Uint8Array): Timecode {
  invariant(bytes.length === 8, 'Expected 8 bytes of timecode data');
  const b0 = bytes[0]!;
  const b1 = bytes[1]!;
  const b2 = bytes[2]!;
  const b3 = bytes[3]!;
  const frames = (b0 & 0xF) + (((b0 >> 4) & 0x3) * 10);
  const dropFrame = ((b0 & (1 << 6)) != 0);
  const seconds = (b1 & 0xF) + (((b1 >> 4) & 0x7) * 10);
  const minutes = (b2 & 0xF) + (((b2 >> 4) & 0x7) * 10);
  const hours = (b3 & 0xF) + (((b3 >> 4) & 0x3) * 10);
  return {
    frames,
    seconds,
    minutes,
    hours,
    dropFrame
  };
}

/**
 *  Serialized timecode to binary format specified in SMPTE ST 12-1
 */
export function serializeTimecode(timecode: Timecode): Uint8Array {
  return new Uint8Array([
    (1 << 7) | // Color frame
    (timecode.dropFrame ? (1 << 6) : 0) |
    ((Math.trunc(timecode.frames / 10.0) & 0x3) << 4) |
    (timecode.frames % 10),

    ((Math.trunc(timecode.seconds / 10.0) & 0x7) << 4) |
    (timecode.seconds % 10),

    ((Math.trunc(timecode.minutes / 10.0) & 0x7) << 4) |
    (timecode.minutes % 10),

    ((Math.trunc(timecode.hours / 10.0) & 0x3) << 4) |
    (timecode.hours % 10),

    0, 0, 0, 0 // Userdata / binary group
  ]);
}

export const validateTimecode = makeValidationFunction<Timecode>((u: unknown) => {
  return {
    hours: validateNumberProperty(u, 'hours'),
    minutes: validateNumberProperty(u, 'minutes'),
    seconds: validateNumberProperty(u, 'seconds'),
    frames: validateNumberProperty(u, 'frames'),
    dropFrame: validateBooleanProperty(u, 'dropFrame'),
  };
});
